import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Calendar, GraduationCap, Award, ChevronDown, ChevronUp } from 'lucide-react';

const EducationStep = ({ formData, updateFormData, onNext, onPrev, onSkip }) => {
  const [educations, setEducations] = useState(formData.education || []);
  const [showForm, setShowForm] = useState(false);
  const [currentEducation, setCurrentEducation] = useState({
    degree: '',
    field: '',
    institution: '',
    location: '',
    startDate: '',
    endDate: '',
    current: false,
    gpa: '',
    achievements: ''
  });
  const [editIndex, setEditIndex] = useState(-1);
  const [errors, setErrors] = useState({});
  const [expandedIndex, setExpandedIndex] = useState(-1);
  
  // Check if any education entries are from parsed resume
  const [hasParsedData, setHasParsedData] = useState(false);
  
  useEffect(() => {
    // Check if we have parsed resume data
    if (formData.resume && formData.resume.parsed && formData.resume.parsedData?.education) {
      // Only set if we don't already have education entries
      if (educations.length === 0) {
        setEducations(formData.resume.parsedData.education);
        setHasParsedData(true);
      }
    }
  }, [formData.resume, educations.length]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch {
      return dateString;
    }
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentEducation(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCurrentEducation(prev => ({
      ...prev,
      [name]: checked,
      // If current education, clear end date
      ...(name === 'current' && checked ? { endDate: '' } : {})
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!currentEducation.degree.trim()) {
      newErrors.degree = 'Degree is required';
    }
    
    if (!currentEducation.institution.trim()) {
      newErrors.institution = 'Institution name is required';
    }
    
    if (!currentEducation.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (!currentEducation.current && !currentEducation.endDate) {
      newErrors.endDate = 'End date is required if not currently studying';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Add or update education
  const handleSaveEducation = () => {
    if (!validateForm()) return;
    
    if (editIndex >= 0) {
      // Update existing education
      const updatedEducations = [...educations];
      updatedEducations[editIndex] = currentEducation;
      setEducations(updatedEducations);
    } else {
      // Add new education
      setEducations(prev => [...prev, currentEducation]);
    }
    
    // Reset form
    setCurrentEducation({
      degree: '',
      field: '',
      institution: '',
      location: '',
      startDate: '',
      endDate: '',
      current: false,
      gpa: '',
      achievements: ''
    });
    setEditIndex(-1);
    setShowForm(false);
    setHasParsedData(false); // No longer using just parsed data
  };
  
  // Edit education
  const handleEditEducation = (index) => {
    setCurrentEducation(educations[index]);
    setEditIndex(index);
    setShowForm(true);
    setExpandedIndex(-1); // Close any expanded view
  };
  
  // Delete education
  const handleDeleteEducation = (index) => {
    const updatedEducations = educations.filter((_, i) => i !== index);
    setEducations(updatedEducations);
    setHasParsedData(false); // No longer using just parsed data
  };
  
  // Toggle expanded view
  const toggleExpanded = (index) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };
  
  // Handle continue
  const handleContinue = () => {
    updateFormData('education', educations);
    onNext();
  };
  
  // Get degree type icon
  const getDegreeIcon = (degree) => {
    const lowerDegree = degree.toLowerCase();
    if (lowerDegree.includes('bachelor')) return '🎓';
    if (lowerDegree.includes('master')) return '📚';
    if (lowerDegree.includes('phd') || lowerDegree.includes('doctor')) return '🔬';
    if (lowerDegree.includes('diploma')) return '📜';
    if (lowerDegree.includes('certificate')) return '📝';
    return '🎓';
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-center mb-2">Your Education Background 🎓</h1>
        <p className="text-gray-600 text-center mb-8">
          Share your educational qualifications and academic achievements
        </p>
        
        {/* Education list */}
        {educations.length > 0 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-800">
                Your Education ({educations.length})
              </h2>
              {hasParsedData && (
                <div className="text-sm text-blue-600 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Added from your resume
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {educations.map((education, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex justify-between">
                      <div>
                        <div className="flex items-center">
                          <span className="mr-2 text-xl">{getDegreeIcon(education.degree)}</span>
                          <h3 className="text-lg font-medium text-gray-800">{education.degree}</h3>
                        </div>
                        <p className="text-gray-600">{education.institution}</p>
                        {education.field && (
                          <p className="text-gray-500 text-sm">{education.field}</p>
                        )}
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <Calendar size={14} className="mr-1" />
                          <span>
                            {formatDate(education.startDate)} - {education.current ? 'Present' : formatDate(education.endDate)}
                          </span>
                          {education.location && (
                            <>
                              <span className="mx-2">•</span>
                              <span>{education.location}</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-2">
                        <button
                          type="button"
                          onClick={() => handleEditEducation(index)}
                          className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteEducation(index)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExpanded(index)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {expandedIndex === index ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded details */}
                    <AnimatePresence>
                      {expandedIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            {education.gpa && (
                              <div className="mb-2">
                                <h4 className="text-sm font-medium text-gray-700">GPA</h4>
                                <p className="text-sm text-gray-600">{education.gpa}</p>
                              </div>
                            )}
                            
                            {education.achievements && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700">Achievements</h4>
                                <p className="text-sm text-gray-600 whitespace-pre-line">{education.achievements}</p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Add education button or form */}
        {!showForm ? (
          <motion.button
            type="button"
            className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center mb-8"
            onClick={() => setShowForm(true)}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <Plus size={18} className="mr-2" />
            Add Education
          </motion.button>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 rounded-lg p-6 mb-8 shadow-sm"
          >
            <h2 className="text-lg font-medium text-gray-800 mb-4">
              {editIndex >= 0 ? 'Edit Education' : 'Add Education'}
            </h2>
            
            <div className="space-y-4">
              {/* Degree */}
              <div>
                <label htmlFor="degree" className="block text-sm font-medium text-gray-700 mb-1">
                  Degree/Certificate*
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <GraduationCap size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="degree"
                    name="degree"
                    value={currentEducation.degree}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-2 border ${errors.degree ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    placeholder="e.g. Bachelor of Technology"
                  />
                </div>
                {errors.degree && <p className="mt-1 text-sm text-red-600">{errors.degree}</p>}
              </div>
              
              {/* Field of Study */}
              <div>
                <label htmlFor="field" className="block text-sm font-medium text-gray-700 mb-1">
                  Field of Study <span className="text-gray-400">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Award size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="field"
                    name="field"
                    value={currentEducation.field}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g. Computer Science"
                  />
                </div>
              </div>
              
              {/* Institution */}
              <div>
                <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1">
                  Institution*
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="institution"
                    name="institution"
                    value={currentEducation.institution}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-2 border ${errors.institution ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    placeholder="e.g. Mumbai University"
                  />
                </div>
                {errors.institution && <p className="mt-1 text-sm text-red-600">{errors.institution}</p>}
              </div>
              
              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                  Location <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={currentEducation.location}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="e.g. Mumbai, India"
                />
              </div>
              
              {/* Education Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Education Period*
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="startDate" className="block text-xs text-gray-500 mb-1">
                      Start Date
                    </label>
                    <input
                      type="month"
                      id="startDate"
                      name="startDate"
                      value={currentEducation.startDate}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.startDate ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    />
                    {errors.startDate && <p className="mt-1 text-xs text-red-600">{errors.startDate}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="endDate" className="block text-xs text-gray-500 mb-1">
                      End Date
                    </label>
                    <input
                      type="month"
                      id="endDate"
                      name="endDate"
                      value={currentEducation.endDate}
                      onChange={handleInputChange}
                      disabled={currentEducation.current}
                      className={`w-full px-3 py-2 border ${errors.endDate ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${currentEducation.current ? 'bg-gray-100' : ''}`}
                    />
                    {errors.endDate && <p className="mt-1 text-xs text-red-600">{errors.endDate}</p>}
                  </div>
                </div>
                
                <div className="mt-2">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="current"
                      checked={currentEducation.current}
                      onChange={handleCheckboxChange}
                      className="rounded border-gray-300 text-yellow-500 focus:ring-yellow-400"
                    />
                    <span className="ml-2 text-sm text-gray-700">I am currently studying here</span>
                  </label>
                </div>
              </div>
              
              {/* GPA */}
              <div>
                <label htmlFor="gpa" className="block text-sm font-medium text-gray-700 mb-1">
                  GPA/Grade <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="gpa"
                  name="gpa"
                  value={currentEducation.gpa}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="e.g. 3.8/4.0 or First Class"
                />
              </div>
              
              {/* Achievements */}
              <div>
                <label htmlFor="achievements" className="block text-sm font-medium text-gray-700 mb-1">
                  Achievements <span className="text-gray-400">(Optional)</span>
                </label>
                <textarea
                  id="achievements"
                  name="achievements"
                  value={currentEducation.achievements}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="e.g. Dean's List, Academic Scholarships, Projects"
                ></textarea>
              </div>
              
              {/* Form buttons */}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setShowForm(false);
                    setCurrentEducation({
                      degree: '',
                      field: '',
                      institution: '',
                      location: '',
                      startDate: '',
                      endDate: '',
                      current: false,
                      gpa: '',
                      achievements: ''
                    });
                    setEditIndex(-1);
                    setErrors({});
                  }}
                >
                  Cancel
                </button>
                
                <motion.button
                  type="button"
                  className="px-4 py-2 bg-black text-white font-medium rounded-md hover:bg-gray-800 transition-colors"
                  onClick={handleSaveEducation}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {editIndex >= 0 ? 'Update' : 'Save'}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Tips */}
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-8">
          <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Pro Tips
          </h3>
          <ul className="text-sm text-blue-700 space-y-1 ml-6 list-disc">
            <li>List your most recent education first</li>
            <li>Include relevant coursework and projects if they align with your career goals</li>
            <li>Mention academic honors, scholarships, and achievements</li>
            <li>For recent graduates, education should be more detailed than for experienced professionals</li>
          </ul>
        </div>
        
        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
            onClick={onPrev}
          >
            Back
          </button>
          
          <div className="flex gap-4">
            <button
              type="button"
              className="px-6 py-2 text-gray-500 font-medium hover:text-gray-700 transition-colors"
              onClick={() => {
                updateFormData('education', educations);
                onSkip();
              }}
            >
              Skip for now
            </button>
            
            <motion.button
              type="button"
              className="px-8 py-3 bg-black text-white font-bold rounded-md shadow-md hover:bg-gray-800 transition-all"
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Continue
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EducationStep;
